import { useState, useEffect } from 'react';

/**
 * Returns a debounced version of `value` that only updates after `delayMs`
 * milliseconds of no further changes.
 *
 * Use for search inputs that trigger API calls — the returned value can be
 * passed directly to a query hook's filter without extra refs or timers.
 *
 * @example
 * ```ts
 * const [keyword, setKeyword] = useState('');
 * const debouncedKeyword = useDebounced(keyword, 400);
 * // debouncedKeyword updates 400ms after the user stops typing
 * const query = useSearchTasksQuery({ keyword: debouncedKeyword || undefined });
 * ```
 */
export function useDebounced<T>(value: T, delayMs: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debouncedValue;
}

/**
 * Like `useDebounced`, but also returns a flush setter that applies a value
 * immediately, bypassing the delay.
 *
 * Use when the same value is updated both per-keystroke (debounce wanted)
 * and by explicit user actions like Apply/Reset buttons (immediate wanted).
 * The flush setter takes the value explicitly because callers typically
 * invoke it in the same tick as the state update that changes `value` —
 * before that update has committed.
 *
 * @example
 * ```ts
 * const [debouncedAssignees, flushAssignees] = useDebouncedWithFlush(
 *   filters.assignees,
 *   400,
 * );
 * // keystroke path: setFilter('assignees', next) — debounced 400ms
 * // Apply button:   applyFilters(partial); flushAssignees(partial.assignees);
 * ```
 */
export function useDebouncedWithFlush<T>(
  value: T,
  delayMs: number,
): [T, (immediate: T) => void] {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return [debouncedValue, setDebouncedValue];
}
